import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { index as serveAd } from '~/actions/App/Http/Controllers/Api/ServeAdController';
import http from '~/shared/http';
import createObserver from '~/utils/adobserver';
import {
    Card,
    type ExternalAdCard,
    type FormatProps,
    Pill,
    Row,
    Sponsorship,
    Strip,
} from './formats';
import Toast from './Toast';

const formats = { sponsorship: Sponsorship, strip: Strip, row: Row, card: Card, pill: Pill };
const toastShownKey = 'poddtoppen_external_toast_shown';

function useExternalAd(slot: string, enabled: boolean) {
    const [ad, setAd] = useState<ExternalAdCard | null>(null);
    const linkRef = useRef<HTMLAnchorElement>(null);

    const fetchAd = useCallback(() => {
        http.get<{ data: ExternalAdCard | null }>(serveAd.url({ query: { s: slot } }))
            .then(({ data }) => setAd(data.data?.external ? data.data : null))
            .catch(() => setAd(null));
    }, [slot]);

    useEffect(() => {
        if (enabled) fetchAd();
    }, [enabled, fetchAd]);

    // Impressions and clicks are measured on the link; a new card replaces an expired one.
    useEffect(() => {
        if (!ad || !linkRef.current) return undefined;
        return createObserver(
            { ...ad, el: linkRef.current, pid: ad.id, slot },
            { hooks: { onExpire: fetchAd } },
        );
    }, [ad, fetchAd, slot]);

    return { ad, linkRef, clear: () => setAd(null) };
}

/** An external (non-podcast) ad in a fixed spot on the page; renders nothing when unsold. */
export function ExternalAd({ slot }: { slot: string }) {
    const { ad, linkRef } = useExternalAd(slot, true);
    if (!ad || ad.format === 'toast') return null;
    const Format = formats[ad.format];

    return <Format ad={ad} linkRef={linkRef as FormatProps['linkRef']} />;
}

/**
 * An external ad that slides in after a short delay, at most once per browser session
 * so it never follows the visitor from page to page.
 */
export function ExternalAdToast({ slot, delay = 3000 }: { slot: string; delay?: number }) {
    const [due, setDue] = useState(false);
    const { ad, linkRef, clear } = useExternalAd(slot, due);

    useEffect(() => {
        if (sessionFlag()) return undefined;
        const timer = setTimeout(() => setDue(true), delay);
        return () => clearTimeout(timer);
    }, [delay]);

    useEffect(() => {
        if (ad) sessionFlag(true);
    }, [ad]);

    return ad?.format === 'toast' ? <Toast ad={ad} linkRef={linkRef} onClosed={clear} /> : null;
}

function sessionFlag(set = false): boolean {
    try {
        if (set) sessionStorage.setItem(toastShownKey, '1');
        return sessionStorage.getItem(toastShownKey) === '1';
    } catch {
        return set;
    }
}
